import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Progress,
  Placeholder,
} from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Phase1PersonalInfoForm from './Phase1PersonalInfoForm';
import Phase2DocumentsForm from './Phase2DocumentsForm';
import Phase3ServicesForm from './Phase3ServicesForm';
import { individualsService } from '../../../services/individualsService';
import { handleApiResponse, handleApiError, ApiResponse } from '../../../utils/apiResponseHandler';

interface WizardState {
  currentPhase: number;
  individualId: string | null;
  phase1Data: any;
  phase2Data: any;
  phase3Data: any;
  isSubmitting: boolean;
  isLoading: boolean;
}

const IndividualWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();

  // Get phase from query parameter (e.g., ?phase=1)
  const phaseParam = searchParams.get('phase');
  const initialPhase = phaseParam ? parseInt(phaseParam, 10) : 1;

  const [wizardState, setWizardState] = useState<WizardState>({
    currentPhase: initialPhase,
    individualId: id || null,
    phase1Data: null,
    phase2Data: null,
    phase3Data: null,
    isSubmitting: false,
    isLoading: !!id,
  });

  const loadIndividualData = useCallback(async (individualId: string) => {
    try {
      setWizardState((prev) => ({ ...prev, isLoading: true }));

      const individual = await individualsService.getById(individualId);

      // Determine current phase: use query parameter if provided, otherwise use registration phase
      // Phase 4 (Completed) should show Phase 3 for editing
      let defaultPhase = individual.registrationPhase || 1;
      if (defaultPhase === 4) {
        defaultPhase = 3; // Completed individuals edit phase 3
      }
      const currentPhase = phaseParam ? parseInt(phaseParam, 10) : defaultPhase;

      // Map phase 1 data (Personal Information)
      const phase1Data = {
        name: individual.name,
        cpf: individual.cpf,
        phone: individual.phone,
        birthDate: individual.birthDate ? individual.birthDate.split('T')[0] : '',
        rg: individual.rg || '',
        email: individual.email || '',
        professionalEntity: individual.professionalEntity || '',
        address: individual.address ? {
          cep: individual.address.cep,
          street: individual.address.street,
          number: individual.address.number,
          complement: individual.address.complement || '',
          neighborhood: individual.address.neighborhood,
          city: individual.address.city,
          state: individual.address.state,
        } : {
          cep: '',
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
        },
      };

      // Map phase 2 data (Documents) - placeholder for now
      const phase2Data = (individual.registrationPhase && individual.registrationPhase >= 2) ? {} : null;

      // Map phase 3 data (Services) - placeholder for now
      const phase3Data = (individual.registrationPhase && individual.registrationPhase >= 3) ? {} : null;

      setWizardState((prev) => ({
        ...prev,
        individualId,
        currentPhase,
        phase1Data,
        phase2Data,
        phase3Data,
        isLoading: false,
      }));

      toast.success(t('Pessoa física carregada com sucesso'));
    } catch (error: any) {
      toast.error(error.message || t('Erro ao carregar pessoa física'));
      setWizardState((prev) => ({ ...prev, isLoading: false }));
      navigate('/apps-crm-individuals');
    }
  }, [t, phaseParam, navigate]);

  // Load existing individual data if editing
  useEffect(() => {
    if (id) {
      loadIndividualData(id);
    }
  }, [id, loadIndividualData]);

  const handlePhase1Submit = async (values: any) => {
    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      let individualId: string;

      if (wizardState.individualId) {
        // Editing existing individual
        individualId = wizardState.individualId;
        const response = await individualsService.update({
          ...values,
          individualId,
        });
        // Handle standardized response if it has severity, otherwise show success
        if (response && typeof response === 'object' && 'severity' in response && 'success' in response) {
          handleApiResponse(response as ApiResponse);
        } else {
          toast.success(t('Informações pessoais atualizadas com sucesso!'));
        }
      } else {
        // Creating new individual
        const result = await individualsService.create(values);
        // Handle standardized response if it has severity, otherwise extract individualId
        if (result && typeof result === 'object' && 'severity' in result && 'success' in result) {
          const apiResponse = result as ApiResponse;
          handleApiResponse(apiResponse);

          // If severity is not "success", it means business validation failed (e.g., duplicate CPF)
          // Don't continue with the flow, just show the notification and stop
          if (apiResponse.severity !== 'success') {
            setWizardState((prev) => ({ ...prev, isSubmitting: false }));
            return;
          }

          // Extract individualId from ApiResponse.data
          individualId = apiResponse.data?.individualId || (result as any).data?.individualId;
          if (!individualId) {
            throw new Error('individualId not found in response');
          }
        } else {
          individualId = (result as any).individualId;
          toast.success(t('Pessoa física criada com sucesso!'));
        }
      }

      // Complete phase 1 and advance to phase 2
      await individualsService.completePhase1(individualId);

      setWizardState((prev) => ({
        ...prev,
        currentPhase: 2,
        individualId,
        phase1Data: values,
        isSubmitting: false,
      }));

      toast.success(t('Fase 1 concluída com sucesso!'));
    } catch (error: any) {
      // Use standardized error handler
      handleApiError(error, t('Erro ao salvar informações pessoais'));
      setWizardState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handlePhase2Submit = async (values: any) => {
    if (!wizardState.individualId) return;

    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      // Complete phase 2 and advance to phase 3
      // Backend will auto-complete phase 1 if needed
      await individualsService.completePhase2(wizardState.individualId);

      setWizardState((prev) => ({
        ...prev,
        currentPhase: 3,
        phase2Data: values,
        isSubmitting: false,
      }));

      toast.success(t('Fase 2 concluída com sucesso!'));
    } catch (error: any) {
      toast.error(error.message || t('Erro ao salvar documentos'));
      setWizardState((prev) => ({
        ...prev,
        phase2Data: values,
        isSubmitting: false
      }));
    }
  };

  const handlePhase3Submit = async (values: any) => {
    if (!wizardState.individualId) return;

    setWizardState((prev) => ({ ...prev, isSubmitting: true }));
    try {
      // Add services if any were selected
      const hasServices = values.selectedServices && values.selectedServices.length > 0;

      if (hasServices) {
        // Map selected service IDs to ServiceSelectionDto format
        const services = values.selectedServices.map((serviceId: string) => ({
          serviceTypeId: serviceId,
          isRecurring: false,
          recurrenceInterval: undefined,
          customPrice: undefined,
          notes: undefined,
        }));

        // Add services to individual
        await individualsService.addServices({
          individualId: wizardState.individualId,
          services,
        });
      }

      // Complete phase 3 and mark registration as complete
      await individualsService.completePhase3(wizardState.individualId, hasServices);

      const successMessage = id
        ? t('Pessoa física atualizada com sucesso!')
        : t('Pessoa física cadastrada com sucesso!');
      toast.success(successMessage);

      // Navigate to individuals list after success
      setTimeout(() => {
        navigate('/apps-crm-individuals');
      }, 1500);
    } catch (error: any) {
      toast.error(error.message || t('Erro ao finalizar cadastro'));
      setWizardState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handlePreviousPhase = () => {
    setWizardState((prev) => ({
      ...prev,
      currentPhase: Math.max(1, prev.currentPhase - 1),
    }));
  };

  const getProgressPercentage = (): number => {
    // Calculate progress based on 3 phases (1, 2, 3)
    const progress = ((Math.min(wizardState.currentPhase, 3) - 1) / 2) * 100;
    return Math.min(progress, 100);
  };

  const isPhaseComplete = (phase: number): boolean => {
    switch (phase) {
      case 1:
        return wizardState.phase1Data !== null || wizardState.individualId !== null;
      case 2:
        // Phase 2 is complete if we have phase2Data OR if current phase is beyond 2
        return wizardState.phase2Data !== null || wizardState.currentPhase > 2;
      case 3:
        return wizardState.phase3Data !== null;
      default:
        return false;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={id ? t('Editar Pessoa Física') : t('Nova Pessoa Física')}
            pageTitle={t('CRM')}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="card-title mb-0">
                      {id ? t('Editar Pessoa Física - Assistente') : t('Cadastro de Pessoa Física - Assistente')}
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted">|</span>
                      <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {t('Progresso')}: {t(`Fase ${Math.min(wizardState.currentPhase, 3)} de 3`)} - {Math.round(getProgressPercentage())}%
                      </span>
                      <Progress
                        value={getProgressPercentage()}
                        color="success"
                        style={{ width: '150px', height: '8px' }}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {/* Wizard Navigation Tabs */}
                  <Nav pills className="nav-pills-custom mb-4" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: wizardState.currentPhase === 1,
                          done: isPhaseComplete(1),
                        })}
                        onClick={() => {
                          if (isPhaseComplete(1)) {
                            setWizardState((prev) => ({ ...prev, currentPhase: 1 }));
                          }
                        }}
                        style={{ cursor: isPhaseComplete(1) ? 'pointer' : 'not-allowed' }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="ri-user-line"></i>
                        </span>
                        <span className="d-none d-sm-block">
                          {isPhaseComplete(1) && <i className="ri-check-line me-1"></i>}
                          {t('1. Informações Pessoais')}
                        </span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: wizardState.currentPhase === 2,
                          done: isPhaseComplete(2),
                        })}
                        onClick={() => {
                          if (isPhaseComplete(1)) {
                            setWizardState((prev) => ({ ...prev, currentPhase: 2 }));
                          }
                        }}
                        style={{ cursor: isPhaseComplete(1) ? 'pointer' : 'not-allowed' }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="ri-file-text-line"></i>
                        </span>
                        <span className="d-none d-sm-block">
                          {isPhaseComplete(2) && <i className="ri-check-line me-1"></i>}
                          {t('2. Documentos')}
                        </span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: wizardState.currentPhase === 3,
                          done: isPhaseComplete(3),
                        })}
                        onClick={() => {
                          // Allow navigation to Phase 3 once individual is created (Phase 1 complete)
                          if (wizardState.individualId) {
                            setWizardState((prev) => ({ ...prev, currentPhase: 3 }));
                          }
                        }}
                        style={{ cursor: wizardState.individualId ? 'pointer' : 'not-allowed' }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="ri-briefcase-line"></i>
                        </span>
                        <span className="d-none d-sm-block">
                          {isPhaseComplete(3) && <i className="ri-check-line me-1"></i>}
                          {t('3. Serviços')}
                        </span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  {/* Wizard Tab Content */}
                  {wizardState.isLoading ? (
                    <div className="py-4">
                      <Card className="mb-4">
                        <CardHeader>
                          <Placeholder animation="glow">
                            <Placeholder xs={6} />
                          </Placeholder>
                        </CardHeader>
                        <CardBody>
                          <Row>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Placeholder animation="glow">
                                <Placeholder xs={4} size="sm" className="mb-2" />
                                <Placeholder xs={12} />
                              </Placeholder>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </div>
                  ) : (
                    <TabContent activeTab={wizardState.currentPhase}>
                      <TabPane tabId={1}>
                        <Phase1PersonalInfoForm
                          key={wizardState.individualId || 'new'}
                          onSubmit={handlePhase1Submit}
                          initialValues={wizardState.phase1Data}
                          isSubmitting={wizardState.isSubmitting}
                        />
                      </TabPane>

                      <TabPane tabId={2}>
                        <Phase2DocumentsForm
                          key={wizardState.individualId || 'new'}
                          onSubmit={handlePhase2Submit}
                          onPrevious={handlePreviousPhase}
                          initialValues={wizardState.phase2Data}
                          isSubmitting={wizardState.isSubmitting}
                          individualId={wizardState.individualId || undefined}
                        />
                      </TabPane>

                      <TabPane tabId={3}>
                        <Phase3ServicesForm
                          onSubmit={handlePhase3Submit}
                          onPrevious={handlePreviousPhase}
                          initialValues={wizardState.phase3Data}
                          isSubmitting={wizardState.isSubmitting}
                          individualId={wizardState.individualId || ''}
                        />
                      </TabPane>
                    </TabContent>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-start mt-4">
                    <Button
                      color="light"
                      onClick={() => navigate('/apps-crm-individuals')}
                    >
                      <i className="ri-arrow-left-line me-1"></i>
                      {t('Cancelar')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default IndividualWizard;
